<script setup>
  import { inject, computed, onMounted } from 'vue'
  import Footer from '../FooterSection.vue'
  import Breadcrumbs from '../BreadCrumbs.vue'
  import TransformOwnerBanner from './TransformOwnerBanner.vue'
  import BcsSearchDropdown from '../BcsSearchDropdown.vue'
  import TransformContinueCard from './TransformContinueCard.vue'
  import TransformCategoryLessonsGrid from './TransformCategoryLessonsGrid.vue'
  import TransformErrorState from './TransformErrorState.vue'
  import { useTransformLearningStore } from '../../stores/TransformLearningStore'
  import { useTransformSearch } from '../../composables/useTransformSearch'
  import { useLessonThumbnails } from '../../composables/useLessonThumbnails'
  import { useTransformAccessGate } from '../../composables/useTransformAccessGate'

  const customNavigation = inject('customNavigation')
  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})

  const store = useTransformLearningStore()
  const { searchItems } = useTransformSearch()
  const { thumbnailUrls, resolveThumbnails } = useLessonThumbnails('TransformModuleDetail')
  const { isAccessChecked, ensureAccess } = useTransformAccessGate()

  const moduleId = computed(() => customNavigation.value.navigationParams.moduleId)

  const moduleData = computed(
    () => store.modulesWithLessonCount.find((m) => m.id === moduleId.value) ?? null
  )

  // Title falls back to the param passed at navigate time (available before store lookup).
  const moduleTitle = computed(
    () => moduleData.value?.title ?? customNavigation.value.navigationParams.moduleTitle ?? ''
  )

  const moduleDescription = computed(() => moduleData.value?.description ?? '')

  // TMD-03: enriched lesson data for this module (quiz data loaded by loadCategoryDetail).
  const detailData = computed(() => store.categoryDetail(moduleId.value))

  // The next incomplete lesson (enriched: has status, durationLabel, quiz).
  const nextLessonEnriched = computed(() => {
    const data = detailData.value
    if (!data?.nextInQueue) return null
    return data.lessons.find(l => l.id === data.nextInQueue.id) ?? null
  })

  // Per-type presentation number of nextInQueue (Lesson N / Quiz N) and its matching
  // total for the "Lesson X of Y" title — from the store's enriched displayNumber.
  const nextLessonIndex = computed(() => nextLessonEnriched.value?.displayNumber ?? null)
  const nextLessonTotal = computed(() => {
    const data = detailData.value
    if (!data || !nextLessonEnriched.value) return null
    return nextLessonEnriched.value.lessonType === 'quiz' ? data.quizTotal : data.videoTotal
  })

  // URL for the TMD-03 "Next Lesson in Queue" card (derived from the resolved map,
  // which is populated in onMounted before isAccessChecked = true so no flash occurs).
  const categoryThumbnailUrl = computed(
    () => (nextLessonEnriched.value ? thumbnailUrls.value[nextLessonEnriched.value.id] ?? null : null)
  )

  // Whether the user has completed no lessons in THIS module — drives the continue card's
  // "Start Here" vs "Next Lesson in Queue" header (per-module scope for the category view).
  const moduleHasNoProgress = computed(
    () => store.progressByModule[detailData.value?.module?.id]?.completedCount === 0
  )

  // A search hands off to the dedicated results page, marking THIS category as the origin
  // so its "Clear Search" returns here rather than to the dashboard.
  function goToSearchResults(term) {
    const t = (term ?? '').trim()
    if (!t) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformSearchResults.name,
      params: {
        searchTerm: t,
        searchOrigin: customNavigation.value.routes.transformModuleDetail.name,
        searchOriginModuleId: moduleId.value,
        searchOriginModuleTitle: moduleTitle.value,
      },
    })
  }

  function goToDashboard() {
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformLearningDashboard.name
    })
  }

  async function loadModuleDetail() {
    toggleLoadingWithMessage(true, null)
    try {
      // Ensure catalogue is loaded (no-op when already cached from a dashboard visit) and
      // refresh the user's progress so completion badges reflect the latest saved state.
      await store.loadDashboard()
      await store.refreshProgress()

      // Defensive: if the module can't be resolved (e.g. moduleId lost/invalid on a refresh
      // with cleared storage), redirect to the dashboard instead of rendering a broken view.
      if (!detailData.value) {
        customNavigation.value.navigate({
          newPage: customNavigation.value.routes.transformLearningDashboard.name
        })
        return
      }

      // Quiz data and thumbnails are independent once the catalogue is loaded — run in parallel.
      await Promise.all([
        store.loadCategoryDetail(moduleId.value),
        resolveThumbnails(detailData.value?.lessons ?? [])
      ])

      // Patch the last breadcrumb item to show the actual module title instead of the static
      // route title ('Category'), now that the module is resolved from the store.
      if (moduleTitle.value) {
        const crumbs = customNavigation.value.breadCrumbs
        if (crumbs.length > 0) {
          crumbs[crumbs.length - 1].title = moduleTitle.value
        }
      }

      isAccessChecked.value = true
    } finally {
      toggleLoadingWithMessage(false, null)
    }
  }

  onMounted(async () => {
    if (!(await ensureAccess())) return
    await loadModuleDetail()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
</script>

<template>
  <div v-if="isAccessChecked && store.error" class="transform-module-detail bcs-page-padding mt-4">
    <Breadcrumbs />
    <TransformErrorState :message="store.error" @retry="loadModuleDetail" />
    <Footer />
  </div>
  <div v-else-if="isAccessChecked" class="transform-module-detail">
    <TransformOwnerBanner />
    <div class="mt-4 bcs-page-padding">
      <Breadcrumbs />

      <!-- Header: two-column — title/desc/back-link on left, search on right -->
      <div class="row align-items-start tmd-header-row">
        <div class="col-12 col-lg-7">
          <h1 class="text-28 tmd-title">
            <med>{{ moduleTitle }}</med>
          </h1>
          <p v-if="moduleDescription" class="text-16 tmd-description text-muted">
            {{ moduleDescription }}
          </p>
          <!-- Back-link (Figma node 21960:115765) -->
          <p class="text-16 tmd-back-link">
            <span
              class="text-primary tmd-back-trigger"
              role="button"
              tabindex="0"
              @click="goToDashboard"
              @keydown.enter.prevent="goToDashboard"
              @keydown.space.prevent="goToDashboard">
              &lt; Back to BCS Transform&#8482; Learning Dashboard
            </span>
          </p>
        </div>

        <!-- Search bar: hands off to the dedicated search-results page on search. -->
        <div class="col-12 col-lg-5 d-flex justify-content-end align-items-start tmd-search-col">
          <div class="tmd-search-wrapper">
            <BcsSearchDropdown
              id="transformModuleSearch"
              placeholder="Search lessons, quizzes, or topics"
              searchButtonText=""
              :items="searchItems"
              :searchFields="['displayTitle', 'description']"
              :arrayTerm="'displayTitle'"
              :maxSuggestions="10"
              :showResults="false"
              @search="(payload) => goToSearchResults(payload.term)"
              @select="(item) => goToSearchResults(item.displayTitle)"
              @clear="() => {}" />
          </div>
        </div>
      </div>
    </div>

    <!-- "Next Lesson in Queue" — full-width eggplant-light band (only with loaded data) -->
    <section v-if="detailData" id="transform-next-lesson" data-section="next-lesson-in-queue">
      <TransformContinueCard
        :lesson="nextLessonEnriched"
        :module="detailData?.module"
        :index="nextLessonIndex"
        :total="nextLessonTotal"
        :thumbnailUrl="categoryThumbnailUrl"
        :isFirstInScope="moduleHasNoProgress"
        showCompleteFallback />
    </section>

    <!-- Lessons grid -->
    <div class="bcs-page-padding">
      <div class="row">
        <div class="col-12">
          <section id="transform-lessons-grid" data-section="lessons-grid" class="tmd-lessons-grid-section">
            <TransformCategoryLessonsGrid
              v-if="detailData"
              :lessons="detailData.lessons"
              :module="detailData.module"
              :total="detailData.total"
              :completedCount="detailData.completedCount"
              :percent="detailData.percent"
              :thumbnailUrls="thumbnailUrls"
              :nextInQueueId="detailData.nextInQueue?.id ?? null" />
          </section>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<style scoped lang="scss">
  .transform-module-detail {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  /* Breadcrumb spacing override — same :deep(.breadcrumbs) pattern as BcsContentLibrary.
     !important is required to beat the shared component's `margin-bottom: 5px !important`. */
  .transform-module-detail :deep(.breadcrumbs) {
    margin-bottom: 32px !important;
  }

  .tmd-title {
    letter-spacing: -0.56px;
    margin-top: 8px;
    margin-bottom: 8px;
  }

  .tmd-description {
    color: var(--bs-gray-700);
    margin-bottom: 20px;
    max-width: 660px;
  }

  .tmd-back-link {
    margin-bottom: 40px;
  }

  .tmd-back-trigger {
    cursor: pointer;
    letter-spacing: -0.32px;

    &:hover {
      text-decoration: underline;
    }
  }

  .tmd-search-col {
    padding-top: 6px; /* align search top with title baseline */
  }

  .tmd-search-wrapper {
    max-width: 470px;
    width: 100%;
  }

  .tmd-lessons-grid-section {
    margin-top: 32px;
    margin-bottom: 48px;
  }
</style>
